import React, { useMemo } from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import useQuickSearch from '../../../hooks/useQuickSearch';
import QuickSearchToolbar from '../../../CoachTools/components/QuickSearchToolbar';
import { StyledDataGrid } from './styles';

const ROWS_PER_PAGE = 25;

const Table = ({
  rows,
  columns,
  pageSize,
  enableSearch,
  loading,
  filterFunction,
  getRowClassName,
  disableRowSelectionOnClick,
  onPageSizeChange,
  autoHeight,
}) => {
  // Get the first filtering stage which returns the rows that match the quick search input.
  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(rows, columns);

  // Get the second filtering stage using custom filter function passed as prop
  const filteredRows = useMemo(() => {
    const rowsToFilter = enableSearch ? quickSearchRows : rows;
    return rowsToFilter.filter(filterFunction);
  }, [
    enableSearch,
    quickSearchRows,
    rows,
    filterFunction,
  ]);

  return (
    <StyledDataGrid
      autoHeight={autoHeight}
      rows={filteredRows}
      columns={columns}
      pageSize={pageSize}
      rowsPerPageOptions={[pageSize || ROWS_PER_PAGE]}
      onPageSizeChange={onPageSizeChange}
      disableSelectionOnClick
      components={{ Toolbar: enableSearch ? QuickSearchToolbar : null }}
      componentsProps={{
        toolbar: enableSearch ? { ...toolbarProps, autoFocus: true } : null,
      }}
      loading={loading}
      getRowClassName={getRowClassName}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
    />
  );
};

Table.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  pageSize: PropTypes.number,
  enableSearch: PropTypes.bool,
  loading: PropTypes.bool,
  filterFunction: PropTypes.func,
  getRowClassName: PropTypes.func,
  disableRowSelectionOnClick: PropTypes.bool,
  onPageSizeChange: PropTypes.func,
  // Size the grid to its rows rather than to its parent, for tables whose parent has no set height.
  autoHeight: PropTypes.bool,
};

Table.defaultProps = {
  pageSize: 25,
  enableSearch: true,
  loading: false,
  filterFunction: () => true,
  getRowClassName: () => '',
  disableRowSelectionOnClick: false,
  onPageSizeChange: undefined,
  autoHeight: false,
};

export default compose(
  observer,
)(Table);
